import React, { useState } from "react";
import {
    Database,
    History,
    MoreHorizontal,
    RefreshCw,
    RotateCcw,
    ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/Popover";
import CustomTooltip from "@/components/shared/CustomTooltip";

import MenuItem from "./MenuItem";

// Row overflow menu — keeps the row compact. Preview and Apply RLS stay
// visible in the row; everything else lives here.
//
// Migration history is deliberately outside the `row.error` gate: a database
// that cannot be read is exactly the one whose history you need.
const RowActionsMenu = ({
    row,
    busyKey,
    catalog,
    onPermApply,
    onAlembicApply,
    onAlembicDowngrade,
    onRlsRollback,
    onHistory,
    onReload,
}) => {
    const [open, setOpen] = useState(false);
    const isBusy = busyKey === row.key;

    // One version back, named. "Apply archived RLS v15" is a number; "v15 —
    // Per-tenant sourcing credentials" is the thing being given up, and the
    // menu hint is where it is read before the confirmation is even opened.
    const canRollBackRls = !row.error && !!row.rlsCurrent && row.rlsCurrent > 1;
    const rollbackTarget = canRollBackRls ? row.rlsCurrent - 1 : null;
    const rollbackEntry = catalog?.rlsVersion(rollbackTarget);

    const handle = (fn) => () => {
        setOpen(false);
        fn(row);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <CustomTooltip text="More actions">
                <PopoverTrigger asChild>
                    <Button
                        aria-label={`More actions for ${row.label}`}
                        className="px-3 py-2 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60"
                    >
                        <MoreHorizontal size={14} aria-hidden="true" />
                    </Button>
                </PopoverTrigger>
            </CustomTooltip>
            <PopoverContent
                align="end"
                side="bottom"
                sideOffset={6}
                collisionPadding={12}
                collisionBoundary={
                    typeof document !== "undefined" ? document.body : undefined
                }
                avoidCollisions
                className="w-64 p-1 overflow-y-auto"
                style={{ maxHeight: "var(--radix-popper-available-height)" }}
            >
                <MenuItem
                    icon={ShieldCheck}
                    label="Sync permissions"
                    hint={
                        row.error
                            ? "Unavailable — this database could not be read"
                            : "Copy the platform's permission catalog into this database"
                    }
                    onClick={handle(onPermApply)}
                    disabled={!!row.error}
                />
                <MenuItem
                    icon={Database}
                    label="Upgrade the schema"
                    hint={
                        row.error
                            ? "Unavailable — this database could not be read"
                            : "Run alembic upgrade head against this database"
                    }
                    onClick={handle(onAlembicApply)}
                    disabled={!!row.error}
                />
                <MenuItem
                    icon={RotateCcw}
                    label="Downgrade one revision"
                    hint={
                        row.error
                            ? "Unavailable — this database could not be read"
                            : "Roll back the last Alembic upgrade on this database"
                    }
                    onClick={handle(onAlembicDowngrade)}
                    disabled={!!row.error}
                />
                <MenuItem
                    icon={RotateCcw}
                    label="Roll back row security"
                    hint={
                        row.error
                            ? "Unavailable — this database could not be read"
                            : !canRollBackRls
                            ? "No earlier version to roll back to"
                            : rollbackEntry
                            ? `Apply archived RLS v${rollbackTarget} — ${rollbackEntry.title} — to this database`
                            : `Apply archived RLS v${rollbackTarget} to this database`
                    }
                    onClick={handle(onRlsRollback)}
                    disabled={!canRollBackRls}
                />
                <MenuItem
                    icon={History}
                    label="Migration history"
                    hint="Recent apply and downgrade attempts, and a retry for the failures"
                    onClick={handle(onHistory)}
                />
                <MenuItem
                    icon={RefreshCw}
                    label="Reload engine"
                    hint="Rebuild the cached connection pool for this database"
                    onClick={handle(onReload)}
                    disabled={isBusy}
                    iconSpinning={isBusy}
                />
            </PopoverContent>
        </Popover>
    );
};

export default RowActionsMenu;
